/**
 * This component shows a table of all the specific trigger executions
 * and also show the questions and responses for a specific trigger execution.
 */
import {
  Space,
  Avatar,
  Button,
  Input,
  Alert,
  Tooltip,
  Menu,
  Dropdown,
  Tabs,
} from "antd";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import TriggerFlowPanels from "../TriggerFlowPanels/TriggerFlowPanels";
import useDiscordTriggers, {
  fetchTriggerQuestionsAndResponses,
} from "../DiscordTriggers/useDiscordTriggers";
import {
  DiscordTriggersRpc,
  TriggerExecutionsRpc,
} from "../../lib/discordTypes";
import { ColumnsType } from "antd/es/table";
import {
  InboxOutlined,
  SearchOutlined,
  TableOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ColumnType, TableProps } from "antd/lib/table";
import {
  FilterConfirmProps,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import { ExportToCsv } from "export-to-csv";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import DotsFishLoading from "../DotsFishLoading/DotsFishLoading";
import FilterResultsSearchBar from "../FilterResultsSearchBar/FilterResultsSearchBar";
import ResponseAggregates from "../ResponseAggregates/ResponseAggregates";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import Paywall from "../Paywall/Paywall";
import { usePaywallStateDispatch } from "../Paywall/PaywallContext";

const dateFormat = "MM/DD/YY";

type Props = {
  trigger: DiscordTriggersRpc;
  disableExportButton?: boolean;
  oneClickExport?: boolean;
  blockExports?: { oneMonth?: string; filteredData?: string };
};

const DiscordTriggerTrack = ({
  trigger,
  disableExportButton = true,
  oneClickExport = false,
  blockExports = {
    oneMonth: "",
    filteredData: "This is restricted to Business users",
  },
}: Props) => {
  const { loadingPlanTier, userPlanTier } = usePaywallStateDispatch();
  const {
    loadTriggerQuestionsAndResponses,
    triggerExecutions,
    triggerQuestions,
    setTriggerQuestions,
    loadTriggerExecutions,
    loadingExec,
    setTriggerExecutions,
  } = useDiscordTriggers();

  const [filteredResponses, setFilteredResponses] = useState<any[]>([]);

  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});
  const navigate = useNavigate();
  const location = useLocation();

  const clearFilters = () => {
    setFilteredInfo({});
    triggerQuestions.columns.map((column: any) => {
      column.filteredValue = null;
      return column;
    });
    setTriggerQuestions({ ...triggerQuestions });
    setFilteredResponses(triggerQuestions.userResponses);
  };

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<any>(null);
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format(dateFormat)
  );
  const [endDate, setEndDate] = useState(
    moment().add(1, "days").format(dateFormat)
  );
  const [loadingExportData, setLoadingExportData] = useState(false);
  const [currentTab, setCurrentTab] = useState("summary");
  const workspace = getWorkspace(getSupaClient());
  const communityId = localStorage.getItem("guild-id");

  /**
   * Used to make the search in a column
   * @param selectedKeys passed down from antd in `filterDropdown`
   * @param confirm function passed down from antd in `filterDropdown`
   * @param dataIndex data index of the column to search passed down from `getColumnSearchProps`
   */
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  /**
   * Handles resets a search field
   * @param clearFilters function passed down from antd in `filterDropdown`
   * @param selectedKeys passed down from antd in `filterDropdown`
   * @param confirm function passed down from antd in `filterDropdown`
   * @param dataIndex dataIndex for the column passed down from `getColumnSearchProps`
   */
  const handleReset = (
    clearFilters: () => void,
    selectedKeys: string[],
    confirm: any,
    dataIndex: any
  ) => {
    clearFilters();
    setSearchText("");
    handleSearch(selectedKeys, confirm, dataIndex);
  };
  /**
   * Creates properties to add to the column data so that custom search will be displayed
   * adapted from code on https://ant.design/components/table/
   * @param dataIndex - the key/dataIndex for the column that needs search props generated
   */
  const getColumnSearchProps = (dataIndex: any): ColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e: { target: HTMLInputElement }) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters &&
              handleReset(
                clearFilters,
                selectedKeys as string[],
                confirm,
                dataIndex
              )
            }
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false,
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  const [tableView, setTableView] = useState<boolean>(true);
  const [tableColumns, setTableColumns] = useState<any>({});
  useEffect(() => {
    setTriggerQuestions({
      columns: [],
      userResponses: [],
    });
    loadTriggerQuestionsAndResponses({
      rootTriggerId: trigger.root_id,
      startDate,
      endDate,
    });
  }, [startDate, endDate]);

  useEffect(() => {
    const newTableColumns = triggerQuestions.columns.map((column: any) => {
      if (column.key === `executionDate` || column.filters)
        return { ...column, filteredValue: filteredInfo[column.key] };
      return { ...column, ...getColumnSearchProps(column.key) };
    });
    setTableColumns(newTableColumns);
  }, [triggerQuestions]);

  const mapInboxEntries = function (triggerExecutions: any[]) {
    return triggerExecutions.map((triggerExecution: any, index: any) => {
      return {
        ...triggerExecution,
        lastStepFinishedAt: triggerExecution.executionDate,
        orderDate: moment(triggerExecution.executionDate).format(
          "MMM D h:mm a"
        ),
        username: triggerExecution.username,
        communityId,
        workspace,
        rootExecutionId: triggerExecution.rootExecutionId,
        rowKey: index,
      };
    });
  };
  const inboxColumns: ColumnsType<TriggerExecutionsRpc> = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (text: string, record: TriggerExecutionsRpc) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div>
            <Avatar
              src={
                record?.avatar
                  ? `https://cdn.discordapp.com/avatars/${record.user_id}/${record.avatar}.png`
                  : "/avatar_discord.png"
              }
              style={{ marginRight: 15 }}
            />
          </div>
          <div style={{ maxWidth: "200px" }}>
            <div
              style={{ color: "#1890ff", cursor: "pointer" }}
              tabIndex={0}
              role="button"
            >
              {text}
            </div>
          </div>
        </div>
      ),
      width: "25%",
    },
    {
      title: "Status",
      dataIndex: "reachedGoal",
      key: "reachedGoal",
      width: "15%",
    },
    {
      title: "Last message sent",
      dataIndex: "lastStepFinishedAt",
      key: "lastStepFinishedAt",
      render: (text) => <p>{moment(text).format("MMM DD h:mm a")}</p>,
      width: "20%",
    },
  ];

  /**
   * Exports a CSV file of the table, to run when the button is clicked
   * @params
   * type:
   *  custom -> exports data currently displayed on the table
   *  oneMonth -> exports data from the last month
   */
  async function exportCSV(type: "custom" | "oneMonth") {
    let rawData: any[];
    let rawColumns: any;

    if (type === "custom") {
      rawData = filteredResponses;
      rawColumns = tableColumns;
    } else {
      setLoadingExportData(true);
      const resOneMonth = await fetchTriggerQuestionsAndResponses({
        rootTriggerId: trigger.root_id,
        startDate: moment().subtract(30, "days").format(dateFormat),
        endDate: moment().add(1, "days").format(dateFormat),
      });
      rawData = resOneMonth?.userResponses || [];
      rawColumns = resOneMonth?.columns || [];
    }

    // this is important to guarantee that the header and data are linked
    const tableHeader = rawColumns.map((col: any) => {
      if (typeof col.title === "string") {
        return { name: col.title, key: col.key };
      }
      if (col.title && col.title?.props && col.title.props?.title) {
        return { name: col.title.props.title, key: col.key };
      } else {
        return { name: "Question", key: col.key };
      }
    });
    //remove unnecessary keys
    const csvData = rawData.map((row) => {
      const newRow = { ...row };
      delete newRow.rowKey;
      delete newRow.rootExecutionId;
      return newRow;
    });
    // first object keys will be taken as headers
    const headers = { ...csvData[0] };
    tableHeader.forEach((col: { name: string; key: string }) => {
      headers[col.key] = col.name;
    });
    // placing headers on index 0 of array
    csvData.unshift(headers);
    const csvExporter = new ExportToCsv({
      filename: `Dots_${trigger.name}_export_${dayjs().format("YYYY-MM-DD")}`,
      useKeysAsHeaders: false,
      showTitle: false,
      showLabels: false,
    });
    csvExporter.generateCsv(csvData);
    setLoadingExportData(false);
    window.analytics.track(`Trigger - data exported`);
  }

  useEffect(() => {
    setFilteredResponses(triggerQuestions.userResponses);
  }, [triggerQuestions.userResponses]);

  useEffect(() => {
    const initialTab = new URLSearchParams(location.search).get("tab");
    const existingTabs = ["summary", "allResponses"];
    // check if tab value obtained from query string exists
    if (typeof initialTab === "string" && existingTabs.includes(initialTab)) {
      setCurrentTab(initialTab);
    }
  }, [location]);

  const Exportmenu = (
    <Menu>
      <Menu.Item
        key="1"
        onClick={() => exportCSV("oneMonth")}
        disabled={
          disableExportButton ||
          Boolean(blockExports.oneMonth) ||
          loadingExportData
        }
      >
        <Tooltip
          title={blockExports.oneMonth ? blockExports.oneMonth : ""}
          placement="left"
          mouseLeaveDelay={0}
          overlayStyle={{ width: "160px" }}
        >
          <p className="w-full m-0">Export past 30 days</p>
        </Tooltip>
      </Menu.Item>
      <Menu.Item
        key="2"
        onClick={() => {}}
        disabled={
          disableExportButton ||
          Boolean(blockExports.filteredData) ||
          loadingExportData
        }
      >
        <Tooltip
          title={blockExports.filteredData ? blockExports.filteredData : ""}
          placement="left"
          mouseLeaveDelay={0}
          overlayStyle={{ width: "160px" }}
        >
          <p className="w-full m-0">Export custom data</p>
        </Tooltip>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className=" -mt-2">
      <Tabs
        activeKey={currentTab}
        onChange={(tab) => {
          navigate(`?tab=${tab}`);
          window.analytics.track(
            `Switched to ${tab} tab in trigger track page`
          );
        }}
        style={{ margin: "0 10px" }}
        type="card"
      >
        <Tabs.TabPane tab="Summary" key="summary">
          <ResponseAggregates
            rootTriggerId={trigger.root_id}
            triggerId={trigger.trigger_id}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="All Responses" key="allResponses">
          <div className="border-l border-r border-b border-gray-100 -mt-4 rounded-b-lg">
            <div className="flex justify-end pt-4 pr-2">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "end",
                  justifyContent: "space-around",
                }}
              >
                <div>
                  <Space>
                    {tableView && (
                      <div style={{ minWidth: "200px" }}>
                        <FilterResultsSearchBar
                          allResponses={triggerQuestions.userResponses}
                          setFilteredResponses={setFilteredResponses}
                          className="w-full"
                        />
                      </div>
                    )}

                    <Button disabled={loadingExec} onClick={clearFilters}>
                      Clear filters
                    </Button>
                    {oneClickExport ? (
                      <Button
                        disabled={loadingExec}
                        icon={<SaveOutlined />}
                        onClick={() => exportCSV("custom")}
                      >
                        Export
                      </Button>
                    ) : (
                      <Dropdown
                        disabled={
                          loadingExec ||
                          disableExportButton ||
                          loadingExportData
                        }
                        overlay={Exportmenu}
                      >
                        <Tooltip
                          title={
                            disableExportButton
                              ? "This is restricted to Pro or Business users"
                              : "Pick an option below"
                          }
                        >
                          <Button
                            disabled={
                              loadingExec ||
                              disableExportButton ||
                              loadingExportData
                            }
                            icon={<SaveOutlined />}
                          >
                            Export
                          </Button>
                        </Tooltip>
                      </Dropdown>
                    )}
                    <Paywall
                      free={
                        <DateRangePicker
                          endDate={endDate}
                          setEndDate={setEndDate}
                          startDate={startDate}
                          setStartDate={setStartDate}
                          isIndependentDateSelector
                          showLoadingAlert={false}
                          displayShowAllOption
                          customOption={{
                            disabled: true,
                            warningMsg: "This is restricted to Business users",
                          }}
                        />
                      }
                      pro={
                        <DateRangePicker
                          endDate={endDate}
                          setEndDate={setEndDate}
                          startDate={startDate}
                          setStartDate={setStartDate}
                          isIndependentDateSelector
                          showLoadingAlert={false}
                          displayShowAllOption
                          customOption={{
                            disabled: true,
                            warningMsg: "This is restricted to Business users",
                          }}
                          rangeOfSelector={{
                            range: "day",
                            selectorDefaultOptions: [
                              { value: 7, disabled: false },
                              {
                                value: 30,
                                disabled: false,
                              },
                            ],
                          }}
                        />
                      }
                      business={
                        <DateRangePicker
                          endDate={endDate}
                          setEndDate={setEndDate}
                          startDate={startDate}
                          setStartDate={setStartDate}
                          isIndependentDateSelector
                          showLoadingAlert={false}
                          displayShowAllOption
                          customOption={{ disabled: false }}
                          rangeOfSelector={{
                            range: "day",
                            selectorDefaultOptions: [
                              { value: 7, disabled: false },
                              {
                                value: 30,
                                disabled: false,
                              },
                            ],
                          }}
                        />
                      }
                      tier={userPlanTier}
                      loading={loadingPlanTier}
                    />
                    <Button
                      disabled={loadingExec}
                      onClick={() => {
                        setTableView(!tableView);

                        clearFilters();
                        window.analytics.track(
                          `Trigger - executions table view changed`
                        );
                      }}
                      type="primary"
                      icon={tableView ? <InboxOutlined /> : <TableOutlined />}
                    >
                      {tableView ? "Inbox" : "Table"} view
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
            <Space
              key={trigger.trigger_id}
              direction="vertical"
              style={{ width: "100%", textAlign: "left" }}
            >
              {loadingExec ? (
                <DotsFishLoading />
              ) : (
                <div>
                  {triggerQuestions.userResponses.length > 0 ? (
                    <TriggerFlowPanels
                      inboxSource={(() => {
                        return mapInboxEntries(filteredResponses);
                      })()}
                      tableView={tableView}
                      inboxColumns={inboxColumns}
                      rowKey={"rowKey"}
                      tableOptions={{
                        rowKey: "rowKey",
                        size: "small",
                        pagination: {
                          defaultPageSize: 50,
                          showSizeChanger: true,
                          pageSizeOptions: ["50", "100", "200"],
                        },
                        onChange: (_pagination, filters) => {
                          setFilteredInfo(filters);
                        },
                      }}
                      questionColumns={triggerQuestions.columns.map(
                        (column: any) => {
                          if (
                            column.key === `executionDate` ||
                            column.filters
                          ) {
                            return {
                              ...column,
                              filteredValue: filteredInfo[column.key],
                            };
                          }

                          return {
                            ...column,
                            ...getColumnSearchProps(column.key),
                          };
                        }
                      )}
                      questionResponses={filteredResponses}
                    />
                  ) : (
                    <div className="mt-5">
                      <Alert
                        message="No published executions yet  or date range is too small."
                        description="You can edit the trigger by clicking Edit on the top right. Note: This only shows the published executions, test executions aren't tracked."
                        type="info"
                        showIcon
                      />
                    </div>
                  )}
                </div>
              )}
            </Space>
          </div>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default DiscordTriggerTrack;
